import React, { useEffect, useState } from 'react';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ChevronDown, ChevronUp, InfoButtonFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Checkbox, colors, TableCell, TableHeading, TableRow } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { ProductLineCardStatus } from 'store/pages/rfq/initiate-rfq/pick-carriers/pageStore';
import CarrierDelegate from 'store/pages/rfq/initiate-rfq/pick-carriers/carrierDelegate';
import { useManageContactsModalStore } from 'store/pages/rfq/initiate-rfq/pick-carriers/manageContactsModalStore';
import {
  CardHeaderStyled,
  CheckboxWrapperStyled,
  LabelStyled,
  LogoCellStyled,
  LogoHeadingStyled,
  LogoStyled,
  LogoWrapperStyled,
  ManageContactsButtonStyled,
  ProductLineButtonStyled,
  ProductLineCardStyled,
  RecipientsErrorStyled,
  RecipientsListStyled,
  StatusTagStyled,
  TableBodyStyled,
  TableHeaderStyled,
  TableRowStyled,
  TableStyled,
  TableWrapperStyled,
} from './styles';
import ManageContactsModal from '../ManageContactsModal';
import {
  PICK_CARRIERS_CARRIER_ROW_CONTACTS_BTN_TEST_ID,
  PICK_CARRIERS_CARRIER_ROW_ERROR_TEST_ID,
  PICK_CARRIERS_CARRIER_ROW_LIST_TEST_ID,
  PICK_CARRIERS_CARRIER_ROW_LOGO_TEST_ID,
  PICK_CARRIERS_CARRIER_ROW_TEST_ID,
  PICK_CARRIERS_PRODUCT_LINE_CARD_TEST_ID,
  PICK_CARRIERS_PRODUCT_LINE_SECTION_TITLE_TEST_ID,
  PICK_CARRIERS_PRODUCT_LINE_STATUS_TEST_ID,
  PICK_CARRIERS_PRODUCT_LINE_TABLE_TEST_ID,
} from '../../constants';

export const ProductLineCardStatusTitles: Record<ProductLineCardStatus, string> = Object.freeze({
  [ProductLineCardStatus.NO_CARRIERS_REQUESTED]: 'No Carriers Requested',
  [ProductLineCardStatus.MISSING_RECIPIENTS]: 'Missing Recipients',
  [ProductLineCardStatus.INCLUDED_IN_REQUEST]: 'Included in Request',
});

interface ProductLineCardProps {
  productLineId: ProductLineId;
  status: ProductLineCardStatus;
  carriers: CarrierDelegate[];
  isSectionCollapsed: boolean;
}

interface CarrierRowProps {
  carrierDelegate: CarrierDelegate;
  productLineId: ProductLineId;
}

export const CarrierRow: React.FC<CarrierRowProps> = observer(({ carrierDelegate, productLineId }) => {
  const { name, logo, checked } = carrierDelegate.carrier;

  const modalStore = useManageContactsModalStore({
    carrier: carrierDelegate.carrier,
    updateRecipients: carrierDelegate.updateRecipients,
  });

  return (
    <TableRowStyled key={name} data-test-id={PICK_CARRIERS_CARRIER_ROW_TEST_ID}>
      <TableCell width="30%">
        <CheckboxWrapperStyled>
          {/* The checkbox label is wrapped in an additional container to correct misalignment caused by the enlarged checkbox icon. */}
          <Checkbox
            id={`${name}_${productLineId}`}
            data-test-id={`${name}-${productLineId}-row-checkbox`}
            label={<LabelStyled>{name}</LabelStyled>}
            checked={checked}
            onChange={carrierDelegate.changeCarrierCheckboxStatus}
          />
        </CheckboxWrapperStyled>
      </TableCell>
      <LogoCellStyled width="18%">
        {/* The logo wrapper applies styles to handle logos with dynamic sizes, eliminating the need for additional configuration. */}
        <LogoWrapperStyled>
          <LogoStyled src={logo} alt={`${name} logo`} data-test-id={PICK_CARRIERS_CARRIER_ROW_LOGO_TEST_ID} />
        </LogoWrapperStyled>
      </LogoCellStyled>
      <TableCell width="30%">
        {carrierDelegate.allRecipients.length === 0 && checked ? (
          <RecipientsErrorStyled data-test-id={PICK_CARRIERS_CARRIER_ROW_ERROR_TEST_ID}>
            <InfoButtonFilled width="2.2rem" height="2.2rem" color={colors.red[600]} />
            No contacts added
          </RecipientsErrorStyled>
        ) : (
          <RecipientsListStyled data-test-id={PICK_CARRIERS_CARRIER_ROW_LIST_TEST_ID}>
            {carrierDelegate.allRecipients.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </RecipientsListStyled>
        )}
      </TableCell>
      <TableCell width="20%">
        <ManageContactsButtonStyled
          color="primary"
          type="text"
          size="small"
          onClick={modalStore?.openManageContactsModal}
          data-test-id={PICK_CARRIERS_CARRIER_ROW_CONTACTS_BTN_TEST_ID}
        >
          Manage Contacts
        </ManageContactsButtonStyled>
        {/* Div can not be a child of table row, so it's wrapped in table cell  */}
        {modalStore && modalStore.showManageContactsModal && <ManageContactsModal store={modalStore} />}
      </TableCell>
    </TableRowStyled>
  );
});

const ProductLineCard: React.FC<ProductLineCardProps> = observer(
  ({ productLineId, status, isSectionCollapsed, carriers }) => {
    const [isSectionClosed, setIsSectionClosed] = useState(isSectionCollapsed);

    useEffect(() => {
      setIsSectionClosed(isSectionCollapsed);
    }, [isSectionCollapsed]);

    const productLineData = PRODUCT_LINES_CONTENT_MAP[productLineId];

    const toggleCarriersSection = (): void => {
      setIsSectionClosed((prev) => !prev);
    };

    return (
      <ProductLineCardStyled $status={status} data-test-id={PICK_CARRIERS_PRODUCT_LINE_CARD_TEST_ID}>
        <CardHeaderStyled>
          <ProductLineButtonStyled
            customColor={colors.neutral[900]}
            type="text"
            size="large"
            color="custom"
            iconSize="3rem"
            leftIconProp={productLineData.icon('3rem')}
            rightIconProp={isSectionClosed ? <ChevronDown /> : <ChevronUp />}
            onClick={toggleCarriersSection}
            data-test-id={PICK_CARRIERS_PRODUCT_LINE_SECTION_TITLE_TEST_ID}
          >
            {productLineData.label}
          </ProductLineButtonStyled>
          <StatusTagStyled $status={status} data-test-id={PICK_CARRIERS_PRODUCT_LINE_STATUS_TEST_ID}>
            {ProductLineCardStatusTitles[status]}
          </StatusTagStyled>
        </CardHeaderStyled>
        <TableWrapperStyled $isSectionClosed={isSectionClosed} data-test-id={PICK_CARRIERS_PRODUCT_LINE_TABLE_TEST_ID}>
          <TableStyled totalElements={carriers.length} minHeight="auto">
            <TableHeaderStyled>
              <TableRow>
                <TableHeading width="30%">Carrier</TableHeading>
                <LogoHeadingStyled width="18%">Logo</LogoHeadingStyled>
                <TableHeading width="30%">Recipients</TableHeading>
                <TableHeading width="20%" />
              </TableRow>
            </TableHeaderStyled>
            <TableBodyStyled>
              {carriers.map((carrierDelegate) => (
                <CarrierRow
                  key={carrierDelegate.carrier.id}
                  carrierDelegate={carrierDelegate}
                  productLineId={productLineId}
                />
              ))}
            </TableBodyStyled>
          </TableStyled>
        </TableWrapperStyled>
      </ProductLineCardStyled>
    );
  },
);

export default ProductLineCard;
